import React from "react";
import { MessageCircle, Award, Heart, Coffee, Sparkles } from "lucide-react";
import { MESSENGER_URL } from "@/data/staycation";
import Seo from "@/components/Seo";

export default function About() {
  const values = [
    { Icon: Heart, title: "Hospitality first", text: "Every guest is welcomed like family — kasi para sa amin, lahat tayo dapat feel at home." },
    { Icon: Sparkles, title: "Clean & cared for", text: "Each unit is hand-checked between stays. Fresh linens, spotless bathrooms, every time." },
    { Icon: Coffee, title: "Local, personal touch", text: "From quick check-ins to honest tips on where to eat — we keep things real and warm." },
    { Icon: Award, title: "30 happy units", text: "Hosting since day one, with hundreds of returning guests across four prime addresses." },
  ];

  return (
    <div data-testid="about-page">
      <Seo
        title="About the Host — Alexander, Airbnb Superhost"
        description="Meet Alexander — Airbnb Superhost behind Xanders Staycations. 30 cozy units across Shell, Shore 1, Shore 2 and South Residences. Hospitality first, replies fast, treats guests like family."
        path="/about"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-14 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="xs-eyebrow">About the Host</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 leading-[1.05]">
            Welcome to <span className="text-[var(--xs-primary)]">Xanders Staycations</span>.
          </h1>
          <p className="mt-5 text-neutral-700 leading-relaxed max-w-xl">
            At Xanders Staycations, I believe everyone deserves a cozy, comfortable, and affordable place to stay. That's why I started this journey — to create spaces where guests can relax, feel at home, and enjoy memorable stays without stretching their budget.
          </p>
          <p className="mt-4 text-neutral-700 leading-relaxed max-w-xl">
            Today, I'm proud to personally manage 30 thoughtfully selected staycation units across Shell Residences, Shore 1, Shore 2, and South Residences — conveniently located near SM Mall of Asia and SM Southmall Las Piñas. Whether you're planning a family vacation, a romantic getaway, a business trip, or a weekend escape, there's a perfect home waiting for you.
          </p>
          <p className="mt-4 text-neutral-700 leading-relaxed max-w-xl">
            When you message us, you're talking directly to me. I personally respond to every Messenger inquiry — usually within minutes — to help you choose the best unit and ensure a smooth, hassle-free booking experience.
          </p>
          <p className="mt-4 text-neutral-700 leading-relaxed max-w-xl">
            <strong>No hidden fees. No surprises.</strong> Just honest service, clean and comfortable accommodations, and warm Filipino hospitality.
          </p>
          <p className="mt-4 text-neutral-700 leading-relaxed max-w-xl">
            My goal is simple: to make every guest <em>feel at home</em> while providing the best value for their stay. I look forward to welcoming you to your home away from home at Xanders Staycations.
          </p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-messenger-cta"
            className="xs-btn-primary mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
          >
            <MessageCircle className="h-4 w-4" /> Say hi on Messenger
          </a>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-[var(--xs-primary-soft)] rounded-[2.5rem] -z-10" aria-hidden />
          <img
            src="https://customer-assets.emergentagent.com/job_affordable-staycay/artifacts/tjsz5ygl_pixel.jpg"
            alt="Alexander, host of Xanders Staycation"
            className="rounded-[2rem] w-full aspect-square object-contain bg-[var(--xs-bg-alt)]"
            loading="lazy"
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v) => (
            <div key={v.title} className="xs-card p-6">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                <v.Icon className="h-5 w-5 text-[var(--xs-primary)]" />
              </div>
              <h3 className="font-display text-lg font-bold mt-4">{v.title}</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24">
        <div className="rounded-3xl bg-[var(--xs-bg-alt)] border border-neutral-200 p-8 sm:p-12">
          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <p className="font-display text-4xl font-black text-[var(--xs-primary)]">30</p>
              <p className="text-sm text-neutral-600">Hand-picked units</p>
            </div>
            <div>
              <p className="font-display text-4xl font-black text-[var(--xs-primary)]">4</p>
              <p className="text-sm text-neutral-600">Prime addresses</p>
            </div>
            <div>
              <p className="font-display text-4xl font-black text-[var(--xs-primary)]">1k+</p>
              <p className="text-sm text-neutral-600">Happy guests hosted</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
